import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tooltip,
    Tabs,
    Tab,
    LinearProgress,
    Snackbar,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    CheckCircle,
    Cancel,
    Pending,
    Visibility,
    BeachAccess,
    EventAvailable,
    Person,
    ChevronLeft,
    ChevronRight,
    Search,
    Edit as EditIcon
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import useLeavesStore from '@store/LeavesStore';
import { useAuth } from '@store/authStore';

// Type definitions
interface LeaveRequest {
    leave_id: number;
    employee_id: number;
    employee_name: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    title: string;
    body?: string;
    attachment?: string;
    status: 'pending' | 'approved' | 'declined';
    approval_time: string | null;
    managed_by?: string | null;
    managed_by_name?: string | null;
    remarks?: string | null;
    created_at: string;
    employee_email?: string;
    // Legacy fields for backward compatibility
    total_days?: number;
    is_approved?: 'pending' | 'accepted' | 'declined';
    reason?: string;
    approval_date?: string | null;
}


const LeavesPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();

    const {
        leaves,
        pendingLeaves,
        upcomingLeaves,
        declinedLeaves,
        withdrawnLeaves,
        leaveBalancesSummary,
        loading,
        error,
        carryForwardPending,  // ✅ New flag
        allLeavesPagination,
        pendingLeavesPagination,
        upcomingLeavesPagination,
        declinedLeavesPagination,
        withdrawnLeavesPagination,
        leaveBalancesPagination,
        currentPageAllLeaves,
        currentPagePending,
        currentPageUpcoming,
        currentPageDeclined,
        currentPageWithdrawn,
        currentPageBalances,
        setPageAllLeaves,
        setPagePending,
        setPageUpcoming,
        setPageDeclined,
        setPageWithdrawn,
        setPageBalances,
        fetchAllLeaveData,
        fetchLeaveBalancesSummary,
        approveLeaveRequest,
        rejectLeaveRequest,
        correctLeaveDates,
        updateLeaveQuota,
        leaveTypes,
        fetchLeaveTypes,
        availableFinancialYears,
        selectedFinancialYear,
        fetchAvailableFinancialYears,
        setSelectedFinancialYear,
    } = useLeavesStore();

    // Get initial tab from URL parameter
    const getInitialTab = () => {
        const tabParam = searchParams.get('tab');
        switch(tabParam) {
            case 'pending': return 1;
            case 'upcoming': return 2;
            case 'declined': return 3;
            case 'withdrawn': return 4;
            case 'balances': return 5;
            default: return 0; // All Leaves
        }
    };

    const [activeTab, setActiveTab] = useState(getInitialTab());
    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState<LeaveRequest | null>(null);
    const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
    const [openViewDialog, setOpenViewDialog] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    // Remarks state for action dialog (Feature 1)
    const [remarks, setRemarks] = useState('');

    // Date correction state (Feature 2)
    const [openDateCorrection, setOpenDateCorrection] = useState(false);
    const [correctedStartDate, setCorrectedStartDate] = useState('');
    const [correctedEndDate, setCorrectedEndDate] = useState('');

    // Manual quota update state (Feature 3)
    const [openQuotaDialog, setOpenQuotaDialog] = useState(false);
    const [quotaEmployee, setQuotaEmployee] = useState<any>(null);
    const [quotaLeaveTypeId, setQuotaLeaveTypeId] = useState<number | ''>('');
    const [quotaValue, setQuotaValue] = useState<number>(0);
    const [quotaTotalLeaves, setQuotaTotalLeaves] = useState<number | ''>('');

    // Search state for each tab
    const [searchAllLeaves, setSearchAllLeaves] = useState('');
    const [searchPending, setSearchPending] = useState('');
    const [searchUpcoming, setSearchUpcoming] = useState('');
    const [searchDeclined, setSearchDeclined] = useState('');
    const [searchWithdrawn, setSearchWithdrawn] = useState('');
    const [searchBalances, setSearchBalances] = useState('');

    // Handle URL parameter changes
    useEffect(() => {
        const newTab = getInitialTab();
        if (newTab !== activeTab) {
            setActiveTab(newTab);
        }
    }, [searchParams]);

    // Fetch data on component mount
    useEffect(() => {
        fetchAllLeaveData();
        fetchLeaveBalancesSummary();
        fetchLeaveTypes();
        fetchAvailableFinancialYears();
    }, [fetchAllLeaveData, fetchLeaveBalancesSummary, fetchLeaveTypes, fetchAvailableFinancialYears]);

    // Debug logging for leave balances
    useEffect(() => {
        console.log('Leave Balances Summary Data:', leaveBalancesSummary);
        console.log('Loading state:', loading);
        console.log('Error state:', error);
    }, [leaveBalancesSummary, loading, error]);

    // Debounced search for leave balances (backend search with 500ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            // Only trigger search when in Leave Balances tab
            if (activeTab === 5) {
                fetchLeaveBalancesSummary(1, undefined, undefined, searchBalances);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [searchBalances, activeTab]);


    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleOpenActionDialog = (leave: LeaveRequest, action: 'approve' | 'reject') => {
        setSelectedLeave(leave);
        setActionType(action);
        setOpenActionDialog(true);
    };

    const handleCloseActionDialog = () => {
        setOpenActionDialog(false);
        setSelectedLeave(null);
        setRemarks('');
    };

    const handleOpenViewDialog = (leave: LeaveRequest) => {
        setSelectedLeave(leave);
        setOpenViewDialog(true);
    };

    const handleCloseViewDialog = () => {
        setOpenViewDialog(false);
        setSelectedLeave(null);
    };

    const handleSubmitAction = async () => {
        if (!selectedLeave || !user?.id) return;

        try {
            const trimmedRemarks = remarks.trim() || null;
            if (actionType === 'approve') {
                await approveLeaveRequest(selectedLeave.leave_id, user.id, trimmedRemarks);
                setSnackbar({
                    open: true,
                    message: 'Leave request approved successfully!',
                    severity: 'success'
                });
            } else {
                await rejectLeaveRequest(selectedLeave.leave_id, user.id, trimmedRemarks);
                setSnackbar({
                    open: true,
                    message: 'Leave request declined successfully!',
                    severity: 'success'
                });
            }
            handleCloseActionDialog();
        } catch (error) {
            console.error('Action error:', error);
            setSnackbar({
                open: true,
                message: 'Failed to process leave request. Please try again.',
                severity: 'error'
            });
        }
    };

    // Word count helper for remarks
    const getWordCount = (text: string) => {
        return text.trim() ? text.trim().split(/\s+/).length : 0;
    };

    // Handle date correction submit
    const handleSubmitDateCorrection = async () => {
        if (!selectedLeave || !correctedStartDate || !correctedEndDate) return;

        if (new Date(correctedEndDate) < new Date(correctedStartDate)) {
            setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' });
            return;
        }

        try {
            await correctLeaveDates(selectedLeave.leave_id, correctedStartDate, correctedEndDate);
            setSnackbar({ open: true, message: 'Leave dates corrected successfully!', severity: 'success' });
            setOpenDateCorrection(false);
            setCorrectedStartDate('');
            setCorrectedEndDate('');
            handleCloseViewDialog();
        } catch (error) {
            console.error('Date correction error:', error);
            setSnackbar({ open: true, message: 'Failed to correct leave dates.', severity: 'error' });
        }
    };

    // Handle quota update submit
    const handleSubmitQuotaUpdate = async () => {
        if (!quotaEmployee || !quotaLeaveTypeId) return;

        try {
            const totalLeavesValue = quotaTotalLeaves !== '' ? Number(quotaTotalLeaves) : null;
            await updateLeaveQuota(quotaEmployee.user_id, quotaLeaveTypeId, quotaValue, totalLeavesValue);
            setSnackbar({ open: true, message: 'Leave quota updated successfully!', severity: 'success' });
            setOpenQuotaDialog(false);
            setQuotaEmployee(null);
            setQuotaLeaveTypeId('');
            setQuotaValue(0);
            setQuotaTotalLeaves('');
        } catch (error) {
            console.error('Quota update error:', error);
            setSnackbar({ open: true, message: 'Failed to update leave quota.', severity: 'error' });
        }
    };

    // Open quota edit dialog for an employee
    const handleOpenQuotaDialog = (employee: any, balance?: any) => {
        setQuotaEmployee(employee);
        if (balance) {
            setQuotaLeaveTypeId(balance.leave_type_id);
            setQuotaValue(balance.leaves_taken);
            setQuotaTotalLeaves(balance.total_leaves);
        } else {
            setQuotaLeaveTypeId('');
            setQuotaValue(0);
            setQuotaTotalLeaves('');
        }
        setOpenQuotaDialog(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handler for leave balances pagination with search
    const handlePageBalancesChange = (newPage: number) => {
        fetchLeaveBalancesSummary(newPage, undefined, undefined, searchBalances);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'accepted': return 'success';
            case 'declined': return 'error';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    const getLeaveTypeColor = (leaveType: string) => {
        const typeColors: { [key: string]: string } = {
            'Annual Leave': '#4caf50',
            'Sick Leave': '#ff9800',
            'Personal Leave': '#2196f3',
            'Maternity Leave': '#e91e63',
            'Paternity Leave': '#9c27b0',
            'Emergency Leave': '#f44336'
        };
        return typeColors[leaveType] || '#757575';
    };

    const calculateDays = (fromDate: string, toDate: string) => {
        const start = new Date(fromDate);
        const end = new Date(toDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Filter functions for frontend search by employee name (for other tabs)
    const filterLeavesByName = (leavesList: LeaveRequest[], searchTerm: string) => {
        if (!searchTerm.trim()) return leavesList;
        return leavesList?.filter((leave: LeaveRequest) =>
            leave.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
        ) || [];
    };

    // Apply filters to each tab's data (frontend search for other tabs)
    const filteredAllLeaves = filterLeavesByName(leaves || [], searchAllLeaves);
    const filteredPendingLeaves = filterLeavesByName(pendingLeaves || [], searchPending);
    const filteredUpcomingLeaves = filterLeavesByName(upcomingLeaves || [], searchUpcoming);
    const filteredDeclinedLeaves = filterLeavesByName(declinedLeaves || [], searchDeclined);
    const filteredWithdrawnLeaves = filterLeavesByName(withdrawnLeaves || [], searchWithdrawn);
    // Leave Balances uses backend search, no frontend filtering needed

    if (loading && !leaves) {
        return (
            <Layout>
                <Container maxWidth="xl">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container maxWidth="xl">
                <Box sx={{ p: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box>
                            <Typography variant="h4" component="h1" gutterBottom>
                                Leave Management
                            </Typography>
                            <Typography variant="subtitle1" color="textSecondary">
                                Manage employee leave requests and track leave balances
                            </Typography>
                        </Box>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            sx={{
                                '& .MuiTab-root.Mui-selected': {
                                    color: (theme) => theme.palette.mode === 'dark' ? '#fff' : 'primary.main',
                                    fontWeight: 600
                                }
                            }}
                        >
                            <Tab
                                label="All Leaves"
                                icon={<BeachAccess />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Pending Requests"
                                icon={<Pending />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Upcoming Leaves"
                                icon={<EventAvailable />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Declined Leaves"
                                icon={<Cancel />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Withdrawn Leaves"
                                icon={<Cancel />}
                                iconPosition="start"
                            />
                            <Tab
                                label="Leave Balances"
                                icon={<Person />}
                                iconPosition="start"
                            />
                        </Tabs>
                    </Box>


                    {/* Tab Content */}
                    {activeTab === 0 && (
                        /* All Leaves Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {/* Search Bar and FY Filter */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    {/* Financial Year Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="all-leaves-fy-label">Financial Year</InputLabel>
                                            <Select
                                                labelId="all-leaves-fy-label"
                                                id="all-leaves-fy-select"
                                                value={selectedFinancialYear || ''}
                                                label="Financial Year"
                                                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                            >
                                                {availableFinancialYears.map((year: string) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFinancialYear && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                Showing data for {selectedFinancialYear}
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by employee name..."
                                        value={searchAllLeaves}
                                        onChange={(e) => setSearchAllLeaves(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Employee</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>From - To</TableCell>
                                                <TableCell>Days</TableCell>
                                                <TableCell>Status</TableCell>
                                                <TableCell>Applied Date</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredAllLeaves?.map((leave: LeaveRequest) => (
                                                <TableRow key={leave.leave_id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {leave.employee_name}
                                                            </Typography>
                                                           
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.leave_type}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getLeaveTypeColor(leave.leave_type),
                                                                color: 'white',
                                                                fontWeight: 'medium'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {calculateDays(leave.start_date, leave.end_date)} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.status}
                                                            color={getStatusColor(leave.status)}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatDate(leave.start_date)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenViewDialog(leave)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {(!filteredAllLeaves || filteredAllLeaves.length === 0) && (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography>{searchAllLeaves ? 'No matching leave requests found' : 'No leave requests found'}</Typography>
                                    </Box>
                                )}
                                {allLeavesPagination && allLeavesPagination.totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPageAllLeaves(currentPageAllLeaves - 1)}
                                            disabled={!allLeavesPagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                            Page {currentPageAllLeaves} of {allLeavesPagination.totalPages}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPageAllLeaves(currentPageAllLeaves + 1)}
                                            disabled={!allLeavesPagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 1 && (
                        /* Pending Requests Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {/* Search Bar and FY Filter */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    {/* Financial Year Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="pending-fy-label">Financial Year</InputLabel>
                                            <Select
                                                labelId="pending-fy-label"
                                                id="pending-fy-select"
                                                value={selectedFinancialYear || ''}
                                                label="Financial Year"
                                                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                            >
                                                {availableFinancialYears.map((year: string) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFinancialYear && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                Showing data for {selectedFinancialYear}
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by employee name..."
                                        value={searchPending}
                                        onChange={(e) => setSearchPending(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Employee</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>From - To</TableCell>
                                                <TableCell>Days</TableCell>
                                                <TableCell align="center">View</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredPendingLeaves?.map((leave: LeaveRequest) => (
                                                <TableRow key={leave.leave_id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {leave.employee_name}
                                                            </Typography>
                                                           
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.leave_type}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getLeaveTypeColor(leave.leave_type),
                                                                color: 'white'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {calculateDays(leave.start_date, leave.end_date)} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenViewDialog(leave)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                color="success"
                                                                startIcon={<CheckCircle />}
                                                                onClick={() => handleOpenActionDialog(leave, 'approve')}
                                                            >
                                                                Approve
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<Cancel />}
                                                                onClick={() => handleOpenActionDialog(leave, 'reject')}
                                                            >
                                                                Reject
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {(!filteredPendingLeaves || filteredPendingLeaves.length === 0) && (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography>{searchPending ? 'No matching pending requests found' : 'No pending leave requests'}</Typography>
                                    </Box>
                                )}
                                {pendingLeavesPagination && pendingLeavesPagination.totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPagePending(currentPagePending - 1)}
                                            disabled={!pendingLeavesPagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                            Page {currentPagePending} of {pendingLeavesPagination.totalPages}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPagePending(currentPagePending + 1)}
                                            disabled={!pendingLeavesPagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 2 && (
                        /* Upcoming Leaves Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {/* Search Bar and FY Filter */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    {/* Financial Year Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="upcoming-fy-label">Financial Year</InputLabel>
                                            <Select
                                                labelId="upcoming-fy-label"
                                                id="upcoming-fy-select"
                                                value={selectedFinancialYear || ''}
                                                label="Financial Year"
                                                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                            >
                                                {availableFinancialYears.map((year: string) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFinancialYear && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                Showing data for {selectedFinancialYear}
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by employee name..."
                                        value={searchUpcoming}
                                        onChange={(e) => setSearchUpcoming(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Employee</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>From - To</TableCell>
                                                <TableCell>Days</TableCell>
                                                <TableCell>Starts In</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredUpcomingLeaves?.map((leave: LeaveRequest) => (
                                                <TableRow key={leave.leave_id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {leave.employee_name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.leave_type}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getLeaveTypeColor(leave.leave_type),
                                                                color: 'white',
                                                                fontWeight: 'medium'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {calculateDays(leave.start_date, leave.end_date)} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" color="info.main" fontWeight="medium">
                                                            {Math.ceil((new Date(leave.start_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenViewDialog(leave)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {(!filteredUpcomingLeaves || filteredUpcomingLeaves.length === 0) && (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography>{searchUpcoming ? 'No matching upcoming leaves found' : 'No upcoming approved leaves'}</Typography>
                                    </Box>
                                )}
                                {upcomingLeavesPagination && upcomingLeavesPagination.totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPageUpcoming(currentPageUpcoming - 1)}
                                            disabled={!upcomingLeavesPagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                            Page {currentPageUpcoming} of {upcomingLeavesPagination.totalPages}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPageUpcoming(currentPageUpcoming + 1)}
                                            disabled={!upcomingLeavesPagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 3 && (
                        /* Declined Leaves Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {/* Search Bar and FY Filter */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    {/* Financial Year Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="declined-fy-label">Financial Year</InputLabel>
                                            <Select
                                                labelId="declined-fy-label"
                                                id="declined-fy-select"
                                                value={selectedFinancialYear || ''}
                                                label="Financial Year"
                                                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                            >
                                                {availableFinancialYears.map((year: string) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFinancialYear && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                Showing data for {selectedFinancialYear}
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by employee name..."
                                        value={searchDeclined}
                                        onChange={(e) => setSearchDeclined(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Employee</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>From - To</TableCell>
                                                <TableCell>Days</TableCell>
                                                <TableCell>Rejected Date</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredDeclinedLeaves?.map((leave: LeaveRequest) => (
                                                <TableRow key={leave.leave_id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {leave.employee_name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.leave_type}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getLeaveTypeColor(leave.leave_type),
                                                                color: 'white',
                                                                fontWeight: 'medium'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {calculateDays(leave.start_date, leave.end_date)} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {leave.approval_time ? formatDate(leave.approval_time) : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenViewDialog(leave)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {(!filteredDeclinedLeaves || filteredDeclinedLeaves.length === 0) && (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography>{searchDeclined ? 'No matching declined requests found' : 'No declined leave requests found'}</Typography>
                                    </Box>
                                )}
                                {declinedLeavesPagination && declinedLeavesPagination.totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPageDeclined(currentPageDeclined - 1)}
                                            disabled={!declinedLeavesPagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                            Page {currentPageDeclined} of {declinedLeavesPagination.totalPages}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPageDeclined(currentPageDeclined + 1)}
                                            disabled={!declinedLeavesPagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 4 && (
                        /* Withdrawn Leaves Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {/* Search Bar and FY Filter */}
                                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                    {/* Financial Year Selector */}
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                                        <FormControl size="small" sx={{ minWidth: 200 }}>
                                            <InputLabel id="withdrawn-fy-label">Financial Year</InputLabel>
                                            <Select
                                                labelId="withdrawn-fy-label"
                                                id="withdrawn-fy-select"
                                                value={selectedFinancialYear || ''}
                                                label="Financial Year"
                                                onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                            >
                                                {availableFinancialYears.map((year: string) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        {selectedFinancialYear && (
                                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                Showing data for {selectedFinancialYear}
                                            </Typography>
                                        )}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search by employee name..."
                                        value={searchWithdrawn}
                                        onChange={(e) => setSearchWithdrawn(e.target.value)}
                                        InputProps={{
                                            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                        }}
                                    />
                                </Box>
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Employee</TableCell>
                                                <TableCell>Leave Type</TableCell>
                                                <TableCell>From - To</TableCell>
                                                <TableCell>Days</TableCell>
                                                <TableCell>Withdrawn Date</TableCell>
                                                <TableCell align="center">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredWithdrawnLeaves?.map((leave: LeaveRequest) => (
                                                <TableRow key={leave.leave_id} hover>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="medium">
                                                                {leave.employee_name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={leave.leave_type}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: getLeaveTypeColor(leave.leave_type),
                                                                color: 'white',
                                                                fontWeight: 'medium'
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box>
                                                            <Typography variant="body2">
                                                                {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {calculateDays(leave.start_date, leave.end_date)} days
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {leave.approval_time ? formatDate(leave.approval_time) : '-'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenViewDialog(leave)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                {(!filteredWithdrawnLeaves || filteredWithdrawnLeaves.length === 0) && (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography>{searchWithdrawn ? 'No matching withdrawn requests found' : 'No withdrawn leave requests found'}</Typography>
                                    </Box>
                                )}
                                {withdrawnLeavesPagination && withdrawnLeavesPagination.totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<ChevronLeft />}
                                            onClick={() => setPageWithdrawn(currentPageWithdrawn - 1)}
                                            disabled={!withdrawnLeavesPagination.hasPreviousPage || loading}
                                        >
                                            Previous
                                        </Button>
                                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                            Page {currentPageWithdrawn} of {withdrawnLeavesPagination.totalPages}
                                        </Box>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            endIcon={<ChevronRight />}
                                            onClick={() => setPageWithdrawn(currentPageWithdrawn + 1)}
                                            disabled={!withdrawnLeavesPagination.hasNextPage || loading}
                                        >
                                            Next
                                        </Button>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {activeTab === 5 && (
                        /* Leave Balances Tab */
                        <Card>
                            <CardContent sx={{ p: 0 }}>
                                {loading ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                            Loading leave balances...
                                        </Typography>
                                    </Box>
                                ) : error ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            Error loading leave balances: {error}
                                        </Alert>
                                        <Button 
                                            variant="outlined" 
                                            onClick={() => fetchLeaveBalancesSummary()}
                                        >
                                            Retry
                                        </Button>
                                    </Box>
                                ) : !leaveBalancesSummary || leaveBalancesSummary.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                        <Typography variant="h6" gutterBottom>
                                            No Leave Balance Data Found
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            This could be because:
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary" component="div" sx={{ mt: 2 }}>
                                            • Employee leave balances haven't been initialized
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            onClick={() => fetchLeaveBalancesSummary()}
                                            sx={{ mt: 2 }}
                                            disabled={loading}
                                            startIcon={loading ? <CircularProgress size={16} /> : null}
                                        >
                                            {loading ? 'Loading...' : 'Refresh Data'}
                                        </Button>
                                    </Box>
                                ) : (
                                    <>
                                        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                                            <Typography variant="h6" gutterBottom>
                                                Employee Leave Balances
                                                {leaveBalancesPagination && ` (${leaveBalancesPagination.totalItems} employees)`}
                                            </Typography>

                                            {/* Financial Year Selector */}
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                                    <InputLabel id="financial-year-label">Financial Year</InputLabel>
                                                    <Select
                                                        labelId="financial-year-label"
                                                        id="financial-year-select"
                                                        value={selectedFinancialYear || ''}
                                                        label="Financial Year"
                                                        onChange={(e) => setSelectedFinancialYear(e.target.value)}
                                                    >
                                                        {availableFinancialYears.map((year: string) => (
                                                            <MenuItem key={year} value={year}>
                                                                {year}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>

                                                {selectedFinancialYear && (
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                                                        Showing data for {selectedFinancialYear}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Carry Forward Pending Warning */}
                                            {carryForwardPending && (
                                                <Alert
                                                    severity="warning"
                                                    sx={{
                                                        mt: 2,
                                                        '& .MuiAlert-message': {
                                                            width: '100%'
                                                        }
                                                    }}
                                                >
                                                    <Typography variant="body2">
                                                        <strong>Carry Forward Pending:</strong> Leave balances for {selectedFinancialYear} are being finalized.
                                                        Carry-forward from the previous financial year has not been processed yet.
                                                        The displayed balances may not include carried forward leaves.
                                                        Please check back soon or contact HR to process carry forward.
                                                    </Typography>
                                                </Alert>
                                            )}

                                            {/* Search Bar */}
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Search by employee name..."
                                                value={searchBalances}
                                                onChange={(e) => setSearchBalances(e.target.value)}
                                                InputProps={{
                                                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                                                }}
                                                sx={{ mt: 2 }}
                                            />
                                        </Box>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Employee</TableCell>
                                                        <TableCell>Leave Type</TableCell>
                                                        <TableCell>Total Allocated</TableCell>
                                                        <TableCell>Leaves Taken</TableCell>
                                                        <TableCell>Remaining</TableCell>
                                                        <TableCell>Usage %</TableCell>
                                                        <TableCell align="center">Edit</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {(leaveBalancesSummary || []).map((employee: any) => (
                                                        employee.leave_balances?.map((balance: any, index: number) => (
                                                            <TableRow key={`${employee.employee_id}-${balance.leave_type_id}`} hover>
                                                                {index === 0 && (
                                                                    <TableCell rowSpan={employee.leave_balances.length}>
                                                                        <Box>
                                                                            <Typography variant="body2" fontWeight="medium">
                                                                                {employee.employee_name}
                                                                            </Typography>
                                                                            <Typography variant="caption" color="textSecondary">
                                                                                {employee.employee_email}
                                                                            </Typography>
                                                                        </Box>
                                                                    </TableCell>
                                                                )}
                                                                <TableCell>
                                                                    <Chip
                                                                        label={balance.leave_type}
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: getLeaveTypeColor(balance.leave_type),
                                                                            color: 'white'
                                                                        }}
                                                                    />
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2" fontWeight="medium">
                                                                        {balance.total_leaves} days
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography variant="body2">
                                                                        {balance.leaves_taken} days
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography
                                                                        variant="body2"
                                                                        color={balance.remaining_leaves <= 2 ? 'error' : 'inherit'}
                                                                        fontWeight={balance.remaining_leaves <= 2 ? 'bold' : 'normal'}
                                                                    >
                                                                        {balance.remaining_leaves} days
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                        <LinearProgress
                                                                            variant="determinate"
                                                                            value={(balance.leaves_taken / balance.total_leaves) * 100}
                                                                            sx={{
                                                                                width: 60,
                                                                                height: 8,
                                                                                borderRadius: 4,
                                                                                '& .MuiLinearProgress-bar': {
                                                                                    backgroundColor: (balance.leaves_taken / balance.total_leaves) > 0.8 ? 'error.main' :
                                                                                                   (balance.leaves_taken / balance.total_leaves) > 0.6 ? 'warning.main' : 'success.main'
                                                                                }
                                                                            }}
                                                                        />
                                                                        <Typography variant="caption" color="textSecondary">
                                                                            {Math.round((balance.leaves_taken / balance.total_leaves) * 100)}%
                                                                        </Typography>
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Tooltip title="Edit Leave Quota">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleOpenQuotaDialog(employee, balance)}
                                                                        >
                                                                            <EditIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </TableCell>
                                                            </TableRow>
                                                        )) || []
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                        {(!leaveBalancesSummary || leaveBalancesSummary.length === 0) && !loading && (
                                            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                                                <Typography>{searchBalances ? 'No matching employees found' : 'No leave balance data available'}</Typography>
                                            </Box>
                                        )}
                                        {leaveBalancesPagination && leaveBalancesPagination.totalPages > 1 && (
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<ChevronLeft />}
                                                    onClick={() => handlePageBalancesChange(currentPageBalances - 1)}
                                                    disabled={!leaveBalancesPagination.hasPreviousPage || loading}
                                                >
                                                    Previous
                                                </Button>
                                                <Box sx={{ px: 2, display: 'flex', alignItems: 'center' }}>
                                                    Page {currentPageBalances} of {leaveBalancesPagination.totalPages}
                                                </Box>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={<ChevronRight />}
                                                    onClick={() => handlePageBalancesChange(currentPageBalances + 1)}
                                                    disabled={!leaveBalancesPagination.hasNextPage || loading}
                                                >
                                                    Next
                                                </Button>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    )}


                    {/* Action Dialog */}
                    <Dialog
                        open={openActionDialog}
                        onClose={handleCloseActionDialog}
                        maxWidth="sm"
                        fullWidth
                        PaperProps={{
                            sx: {
                                maxHeight: '60vh',
                                height: 'auto'
                            }
                        }}
                    >
                        <DialogTitle>
                            {actionType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                overflowY: 'auto',
                                maxHeight: 'calc(75vh - 140px)'
                            }}
                        >
                            {selectedLeave && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Employee:</strong> {selectedLeave.employee_name}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Leave Type:</strong> {selectedLeave.leave_type}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Period:</strong> {formatDate(selectedLeave.start_date)} - {formatDate(selectedLeave.end_date)}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Days:</strong> {calculateDays(selectedLeave.start_date, selectedLeave.end_date)} days
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Title:</strong> {selectedLeave.title}
                                    </Typography>
                                    {selectedLeave.body && (
                                        <Typography variant="body1" gutterBottom>
                                            <strong>Description:</strong> {selectedLeave.body}
                                        </Typography>
                                    )}
                                    {/* Remarks/Comments Box */}
                                    <Box sx={{ mt: 2 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Comments / Remarks (optional)"
                                            placeholder="Add any comments or remarks..."
                                            value={remarks}
                                            onChange={(e) => {
                                                const words = e.target.value.trim().split(/\s+/);
                                                if (words.length <= 100 || e.target.value.length < remarks.length) {
                                                    setRemarks(e.target.value);
                                                }
                                            }}
                                            helperText={`${getWordCount(remarks)} / 100 words`}
                                            error={getWordCount(remarks) > 100}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseActionDialog}>Cancel</Button>
                            <Button
                                onClick={handleSubmitAction}
                                variant="contained"
                                color={actionType === 'approve' ? 'success' : 'error'}
                                disabled={loading || getWordCount(remarks) > 100}
                            >
                                {loading ? <CircularProgress size={20} /> : (actionType === 'approve' ? 'Approve' : 'Reject')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* View Dialog */}
                    <Dialog
                        open={openViewDialog}
                        onClose={handleCloseViewDialog}
                        maxWidth="md"
                        fullWidth
                        PaperProps={{
                            sx: {
                                height: '500px',
                                maxHeight: '75vh'
                            }
                        }}
                    >
                        <DialogTitle sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            pb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <Typography variant="h5" component="div" fontWeight="600">
                                Leave Request Details
                            </Typography>
                            {selectedLeave && (
                                <Chip
                                    label={selectedLeave.status.toUpperCase()}
                                    color={
                                        selectedLeave.status === 'approved' ? 'success' :
                                        selectedLeave.status === 'declined' ? 'error' :
                                        'warning'
                                    }
                                    sx={{ fontWeight: 'bold' }}
                                />
                            )}
                        </DialogTitle>
                        <DialogContent
                            sx={{
                                overflowY: 'auto',
                                p: 0,
                                '&::-webkit-scrollbar': {
                                    width: '8px'
                                },
                                '&::-webkit-scrollbar-track': {
                                    background: '#f1f1f1'
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    background: '#888',
                                    borderRadius: '4px'
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: '#555'
                                }
                            }}
                        >
                            {selectedLeave && (
                                <Box>
                                    {/* Employee Information Section */}
                                    <Box sx={{ p: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                                        <Typography variant="overline" color="text.secondary" fontWeight="600">
                                            Employee Information
                                        </Typography>
                                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                <Typography variant="body1" fontWeight="500">
                                                    {selectedLeave.employee_name}
                                                </Typography>
                                            </Box>
                                            {selectedLeave.employee_email && (
                                                <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                                                    {selectedLeave.employee_email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Leave Details Section */}
                                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                                        <Typography variant="overline" color="text.secondary" fontWeight="600">
                                            Leave Details
                                        </Typography>
                                        <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Leave Type
                                                </Typography>
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Chip
                                                        label={selectedLeave.leave_type}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: getLeaveTypeColor(selectedLeave.leave_type),
                                                            color: 'white',
                                                            fontWeight: 'medium'
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Duration
                                                </Typography>
                                                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                                                    {calculateDays(selectedLeave.start_date, selectedLeave.end_date)} days
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Start Date
                                                </Typography>
                                                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                                                    {formatDate(selectedLeave.start_date)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    End Date
                                                </Typography>
                                                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                                                    {formatDate(selectedLeave.end_date)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    Applied On
                                                </Typography>
                                                <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                                                    {formatDate(selectedLeave.created_at)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Reason Section */}
                                    <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                                        <Typography variant="overline" color="text.secondary" fontWeight="600">
                                            Reason for Leave
                                        </Typography>
                                        <Box sx={{ mt: 2 }}>
                                            <Typography variant="body1" fontWeight="500" gutterBottom>
                                                {selectedLeave.title}
                                            </Typography>
                                            {selectedLeave.body && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-line' }}>
                                                    {selectedLeave.body}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    {/* Approval/Decline Information Section */}
                                    {(selectedLeave.status === 'approved' || selectedLeave.status === 'declined') && (
                                        <Box sx={{ p: 3, bgcolor: selectedLeave.status === 'approved' ? 'success.50' : 'error.50' }}>
                                            <Typography variant="overline" color="text.secondary" fontWeight="600">
                                                {selectedLeave.status === 'approved' ? 'Approval Information' : 'Decline Information'}
                                            </Typography>
                                            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
                                                {selectedLeave.managed_by_name && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {selectedLeave.status === 'approved' ? 'Approved By' : 'Declined By'}
                                                        </Typography>
                                                        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Person sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                            <Typography variant="body1" fontWeight="500">
                                                                {selectedLeave.managed_by_name}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                                {selectedLeave.approval_time && (
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {selectedLeave.status === 'approved' ? 'Approved On' : 'Declined On'}
                                                        </Typography>
                                                        <Typography variant="body1" fontWeight="500" sx={{ mt: 0.5 }}>
                                                            {formatDate(selectedLeave.approval_time)}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {selectedLeave.remarks && (
                                                <Box sx={{ mt: 2 }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Remarks
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-line', fontStyle: 'italic' }}>
                                                        {selectedLeave.remarks}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2, justifyContent: 'space-between' }}>
                            <Box>
                                {selectedLeave && (selectedLeave.status === 'pending' || selectedLeave.status === 'approved') && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => {
                                            setCorrectedStartDate(selectedLeave.start_date.split('T')[0]);
                                            setCorrectedEndDate(selectedLeave.end_date.split('T')[0]);
                                            setOpenDateCorrection(true);
                                        }}
                                    >
                                        Correct Dates
                                    </Button>
                                )}
                            </Box>
                            <Button onClick={handleCloseViewDialog} variant="outlined">Close</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Date Correction Dialog */}
                    <Dialog
                        open={openDateCorrection}
                        onClose={() => setOpenDateCorrection(false)}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>Correct Leave Dates</DialogTitle>
                        <DialogContent>
                            {selectedLeave && (
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Correcting dates for {selectedLeave.employee_name}'s leave request
                                    </Typography>
                                    <TextField
                                        label="New Start Date"
                                        type="date"
                                        value={correctedStartDate}
                                        onChange={(e) => setCorrectedStartDate(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    <TextField
                                        label="New End Date"
                                        type="date"
                                        value={correctedEndDate}
                                        onChange={(e) => setCorrectedEndDate(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                    {correctedStartDate && correctedEndDate && (
                                        <Typography variant="body2" color="info.main">
                                            New duration: {calculateDays(correctedStartDate, correctedEndDate)} days
                                        </Typography>
                                    )}
                                    {selectedLeave.status === 'approved' && (
                                        <Alert severity="warning" sx={{ mt: 1 }}>
                                            This leave is already approved. Correcting dates will automatically recalculate the leave balance.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenDateCorrection(false)}>Cancel</Button>
                            <Button
                                onClick={handleSubmitDateCorrection}
                                variant="contained"
                                disabled={loading || !correctedStartDate || !correctedEndDate}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Save Dates'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Manual Quota Update Dialog */}
                    <Dialog
                        open={openQuotaDialog}
                        onClose={() => setOpenQuotaDialog(false)}
                        maxWidth="xs"
                        fullWidth
                    >
                        <DialogTitle>Update Leave Quota</DialogTitle>
                        <DialogContent>
                            {quotaEmployee && (
                                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Updating leave balance for <strong>{quotaEmployee.employee_name}</strong>
                                    </Typography>
                                    <TextField
                                        select
                                        label="Leave Type"
                                        value={quotaLeaveTypeId}
                                        onChange={(e) => {
                                            const newTypeId = Number(e.target.value);
                                            setQuotaLeaveTypeId(newTypeId);
                                            // Auto-fill current values if available
                                            const existingBalance = quotaEmployee.leave_balances?.find(
                                                (b: any) => b.leave_type_id === newTypeId
                                            );
                                            setQuotaValue(existingBalance ? existingBalance.leaves_taken : 0);
                                            setQuotaTotalLeaves(existingBalance ? existingBalance.total_leaves : '');
                                        }}
                                        fullWidth
                                        SelectProps={{ native: true }}
                                    >
                                        <option value="">Select leave type</option>
                                        {(leaveTypes || []).map((lt: any) => (
                                            <option key={lt.id} value={lt.id}>{lt.leave_name}</option>
                                        ))}
                                    </TextField>
                                    <TextField
                                        label="Total Allocated (days)"
                                        type="number"
                                        value={quotaTotalLeaves}
                                        onChange={(e) => setQuotaTotalLeaves(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
                                        fullWidth
                                        inputProps={{ min: 0 }}
                                        helperText="Leave empty to use the default allocation from the leave type"
                                    />
                                    <TextField
                                        label="Leaves Taken (days)"
                                        type="number"
                                        value={quotaValue}
                                        onChange={(e) => setQuotaValue(Math.max(0, Number(e.target.value)))}
                                        fullWidth
                                        inputProps={{ min: 0 }}
                                    />
                                    <Alert severity="info">
                                        Set a custom total allocation for employees who joined mid-year. "Leaves Taken" tracks days already used.
                                    </Alert>
                                </Box>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setOpenQuotaDialog(false)}>Cancel</Button>
                            <Button
                                onClick={handleSubmitQuotaUpdate}
                                variant="contained"
                                disabled={loading || !quotaLeaveTypeId}
                            >
                                {loading ? <CircularProgress size={20} /> : 'Update Quota'}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Success/Error Snackbar */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={2000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        sx={{
                            top: '50% !important',
                            left: '50% !important',
                            transform: 'translate(-50%, -50%) !important'
                        }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            sx={{
                                minWidth: '300px',
                                backgroundColor: snackbar.severity === 'success' ? '#4caf50' : '#f44336',
                                color: 'white',
                                '& .MuiAlert-icon': {
                                    color: 'white'
                                }
                            }}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Container>
        </Layout>
    );
};

export default LeavesPage;
